import { TypeChecker } from '@attain-apps/type-guards'

import type * as types from './types'

/**
 * {@link https://ngrx.io/docs State} of this feature
 */
export interface State {
  error?: Error
  /** Methodology as last loaded from the backend */
  methodology?: State.Methodology
  /** Methodology as edited by the user. `undefined` while the user hasn't touched it */
  editedMethodology?: State.Methodology
  isLoading: boolean
}

export namespace State {
  /** @see {@link types.Methodology} */
  export type Methodology = types.Methodology

  export const INITIAL: Readonly<State> = {
    editedMethodology: undefined,
    error: undefined,
    isLoading: false,
    methodology: undefined,
  }

  /**
   * Whether the methodology ended up different from what was loaded.
   *
   * Editing back to where it started is not a change, so nothing should be
   * submitted for it.
   */
  export function hasChanged(state: State): boolean {
    return (
      TypeChecker.isNotNullish(state.editedMethodology) &&
      state.editedMethodology !== state.methodology
    )
  }

  /** Marks a functional usage request as in flight */
  export function onRequest(state: State): State {
    return { ...state, error: undefined, isLoading: true }
  }

  /** Records the methodology the backend responded with */
  export function onSuccess(
    methodology: Methodology | undefined,
    state: State,
  ): State {
    return { ...state, error: undefined, isLoading: false, methodology }
  }

  /** Records a failed functional usage request */
  export function onError(error: Error, state: State): State {
    return { ...state, error, isLoading: false }
  }

  /** Clears the in flight marker for a request that never ran */
  export function onCancellation(state: State): State {
    return { ...state, isLoading: false }
  }
}
